import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, Btn, Input, Select } from "../components/UI";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Vote, Search, ShieldCheck, Loader2,
  Landmark, Building2, Users2, Flag, Scale, Megaphone, Globe, Briefcase, HandHeart, Radio, UserCheck, MailCheck,
} from "lucide-react";

const ENTITY_TYPES = [
  { value: "candidate", label: "Candidate", icon: UserCheck },
  { value: "candidate_committee", label: "Candidate Committee", icon: Vote },
  { value: "pac", label: "PAC", icon: Landmark },
  { value: "super_pac", label: "Super PAC", icon: Building2 },
  { value: "party_committee", label: "Party Committee", icon: Flag },
  { value: "ie_committee", label: "Independent Expenditure Committee", icon: Scale },
  { value: "ballot_measure_committee", label: "Ballot Measure Committee", icon: Megaphone },
  { value: "issue_advocacy", label: "Issue Advocacy Organization", icon: HandHeart },
  { value: "public_affairs", label: "Public Affairs Organization", icon: Globe },
  { value: "political_org", label: "Political Organization / Movement", icon: Users2 },
  { value: "agency", label: "Consultant / Political Agency", icon: Briefcase },
  { value: "government", label: "Government / Public Information", icon: Radio },
];

const ROLES = ["campaign_admin", "campaign_manager", "treasurer", "compliance_officer", "media_buyer",
  "fundraising_manager", "communications_director", "digital_director", "staff", "consultant", "read_only"];

const STEPS = ["Who do you represent?", "Find your campaign", "Details & contact", "Select your race", "Verify email"];

export default function EntityOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    entity_type: "", display_name: "", candidate_name: "", i_am_the_candidate: false,
    my_role: "campaign_admin", official_email: "", website: "", state: "", office: "",
    district: "", fec_candidate_id: null, race_id: null,
  });
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [entity, setEntity] = useState(null);
  const [elections, setElections] = useState([]);
  const [eq, setEq] = useState({ q: "", state: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [reqForm, setReqForm] = useState(null);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCandidateType = ["candidate", "candidate_committee"].includes(form.entity_type);

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try { const { data } = await api.get(`/political-os/search?q=${encodeURIComponent(q.trim())}`); setResults(data); }
    catch (e) { toast.error(errMsg(e, "Search failed")); }
    finally { setSearching(false); }
  };

  const pickFec = (c) => {
    const name = c.name.split(",").reverse().map(s => s.trim()).join(" ")
      .toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase());
    setForm(f => ({
      ...f, candidate_name: name,
      display_name: f.display_name || `${name.split(" ").pop()} for ${c.office_label === "U.S. Senate" ? "Senate" : "Congress"}`,
      state: c.state, office: c.office_label, district: c.district,
      fec_candidate_id: c.fec_candidate_id, race_id: c.race_id,
    }));
    toast.success(`Matched to FEC record ${c.fec_candidate_id} — race pre-selected`);
    setStep(2);
  };

  const register = async (confirmDuplicate = false) => {
    if (!form.display_name.trim()) return toast.error("Campaign / organization name is required");
    if (!form.official_email.includes("@")) return toast.error("Official campaign email is required");
    setSaving(true);
    try {
      const { data } = await api.post("/political-os/register", { ...form, confirm_duplicate: confirmDuplicate });
      if (!data.created) { setDuplicates(data.duplicates); return; }
      setDuplicates(null);
      setEntity(data.entity);
      toast.success("Political entity created — you're the " + (data.my_roles || []).join(" + ").replace(/_/g, " "));
      if (form.race_id) setStep(4); else { setStep(3); loadElections(form.state || ""); }
    } catch (e) { toast.error(errMsg(e, "Could not register entity")); }
    finally { setSaving(false); }
  };

  const loadElections = async (state = eq.state, query = eq.q) => {
    try {
      const { data } = await api.get(`/political-os/elections/search?q=${encodeURIComponent(query)}&state=${encodeURIComponent(state)}`);
      setElections(data.elections);
    } catch { setElections([]); }
  };

  const attachRace = async (raceId) => {
    try {
      await api.post(`/political-os/entities/${entity.id}/race`, { race_id: raceId });
      toast.success("Race attached — Race Intelligence is being prepared");
      setStep(4);
    } catch (e) { toast.error(errMsg(e, "Could not attach race")); }
  };

  const requestElection = async () => {
    try {
      const { data } = await api.post("/political-os/elections/request", reqForm);
      toast.success(data.message);
      await attachRace(data.election.id);
    } catch (e) { toast.error(errMsg(e, "Could not submit election request")); }
  };

  const sendOtp = async () => {
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verify-email/send`);
      setOtpSent(true);
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e, "Could not send code")); }
  };

  const confirmOtp = async () => {
    try {
      await api.post(`/political-os/entities/${entity.id}/verify-email/confirm`, { code: otp });
      setVerified(true);
      toast.success("Official email verified");
      if (form.website) api.post(`/political-os/entities/${entity.id}/verify-website`).catch(() => {});
    } catch (e) { toast.error(errMsg(e, "Verification failed")); }
  };

  const finish = () => { window.location.href = "/dashboard"; };

  useEffect(() => { if (step === 3) loadElections(form.state || ""); }, [step]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white flex items-start justify-center p-6 pt-12">
      <div className="w-full max-w-3xl" data-testid="entity-onboarding">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
            <Vote className="w-5 h-5 text-slate-900" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-medium">Political Onboarding</div>
            <div className="font-display text-2xl font-bold">{STEPS[step]}</div>
          </div>
        </div>

        <div className="flex mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? "bg-cyan-400 text-slate-900" : i === step ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900" : "bg-white/5 text-slate-500 border border-white/10"}`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? "bg-cyan-400" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        <Card>
          {step === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="entity-type-grid">
              {ENTITY_TYPES.map(t => (
                <button key={t.value} data-testid={`entity-type-${t.value}`}
                  onClick={() => { update("entity_type", t.value); setStep(1); }}
                  className={`text-left p-4 rounded-xl border transition-colors ${form.entity_type === t.value ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/40 bg-white/[0.02]"}`}>
                  <t.icon className="w-5 h-5 text-cyan-300 mb-2" />
                  <div className="text-sm font-semibold leading-tight">{t.label}</div>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="entity-search-step">
              <p className="text-sm text-slate-400">Search first — your campaign may already be known to TotalReach from public FEC filings.</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
                    placeholder={isCandidateType ? "Candidate name…" : "Organization or committee name…"}
                    data-testid="entity-search-input"
                    className="w-full bg-slate-900/80 border border-white/15 rounded-xl pl-10 pr-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none" />
                </div>
                <Btn onClick={search} disabled={searching} data-testid="entity-search-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                </Btn>
              </div>
              {results && (
                <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="entity-search-results">
                  {results.fec_candidates.map(c => (
                    <div key={c.fec_candidate_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{c.name}</div>
                        <div className="text-[11px] text-slate-500">{c.party} · {c.office_label} {c.state}{c.district && c.district !== "00" ? `-${c.district}` : ""} · {c.incumbent_challenge} · <span className="text-slate-600">via {c.source}</span></div>
                      </div>
                      <Btn variant="secondary" onClick={() => pickFec(c)} data-testid={`pick-fec-${c.fec_candidate_id}`}>This is my race</Btn>
                    </div>
                  ))}
                  {results.entities.map(e => (
                    <div key={e.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.04]">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{e.public_campaign_name || e.legal_org_name}</div>
                        <div className="text-[11px] text-amber-300/80">Already on TotalReach — ask its administrator to invite you</div>
                      </div>
                    </div>
                  ))}
                  {results.fec_candidates.length === 0 && results.entities.length === 0 && (
                    <div className="text-sm text-slate-500 py-2">No matches — register below.</div>
                  )}
                </div>
              )}
              <div className="flex justify-between pt-2">
                <Btn variant="secondary" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4" /> Back</Btn>
                <Btn onClick={() => setStep(2)} data-testid="register-new-btn">Register new <ArrowRight className="w-4 h-4" /></Btn>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="entity-details-step">
              <Input label="Campaign / organization name" value={form.display_name}
                onChange={e => update("display_name", e.target.value)} placeholder="Jane Smith for Congress" data-testid="entity-display-name" />
              {isCandidateType && (
                <>
                  <Input label="Candidate name" value={form.candidate_name}
                    onChange={e => update("candidate_name", e.target.value)} data-testid="entity-candidate-name" />
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Are you the candidate?</label>
                    <div className="flex gap-2">
                      <button onClick={() => update("i_am_the_candidate", true)} data-testid="i-am-candidate-yes"
                        className={`flex-1 py-2.5 rounded-xl border text-sm transition-colors ${form.i_am_the_candidate ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-slate-400"}`}>
                        Yes, I am the candidate
                      </button>
                      <button onClick={() => update("i_am_the_candidate", false)} data-testid="i-am-candidate-no"
                        className={`flex-1 py-2.5 rounded-xl border text-sm transition-colors ${!form.i_am_the_candidate ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-slate-400"}`}>
                        No, I represent the candidate
                      </button>
                    </div>
                    {form.i_am_the_candidate && <p className="text-[11px] text-emerald-300/80 mt-1.5">You'll get candidate + campaign admin roles — invite a manager, treasurer or staff anytime later.</p>}
                  </div>
                  {!form.i_am_the_candidate && (
                    <Select label="Your role" value={form.my_role} onChange={e => update("my_role", e.target.value)} data-testid="entity-my-role">
                      {ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </Select>
                  )}
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Input label="Official campaign email (required)" type="email" value={form.official_email}
                  onChange={e => update("official_email", e.target.value)} placeholder="manager@smithforcongress.com" data-testid="entity-official-email" />
                <Input label="Official website" value={form.website}
                  onChange={e => update("website", e.target.value)} placeholder="smithforcongress.com" data-testid="entity-website" />
                <Input label="State" value={form.state} onChange={e => update("state", e.target.value.toUpperCase())} placeholder="TX" />
                <Input label="Office" value={form.office} onChange={e => update("office", e.target.value)} placeholder="U.S. House / State Senate / City Council" />
              </div>
              {form.fec_candidate_id && (
                <div className="text-xs text-emerald-300 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Matched to FEC filing {form.fec_candidate_id} — official record check will pass automatically</div>
              )}
              {duplicates && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-4" data-testid="duplicate-warning">
                  <div className="text-sm font-semibold text-amber-300 mb-2">This campaign may already exist</div>
                  {duplicates.map(d => (
                    <div key={d.id} className="text-xs text-slate-300 mb-1">• {d.public_campaign_name || d.legal_org_name} ({(d.entity_type || "").replace(/_/g, " ")}{d.state ? ` · ${d.state}` : ""})</div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <Btn variant="secondary" onClick={() => setDuplicates(null)}>Cancel</Btn>
                    <Btn onClick={() => register(true)} data-testid="confirm-duplicate-btn">Create separate entity anyway</Btn>
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <Btn variant="secondary" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4" /> Back</Btn>
                <Btn onClick={() => register(false)} disabled={saving} data-testid="entity-register-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Create entity <ArrowRight className="w-4 h-4" />
                </Btn>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="race-select-step">
              <p className="text-sm text-slate-400">Which election are you participating in? Federal races are loaded from official FEC filings.</p>
              <div className="flex gap-2">
                <Input value={eq.state} onChange={e => setEq(s => ({ ...s, state: e.target.value.toUpperCase() }))} placeholder="State (TX)" className="w-28" data-testid="election-state-filter" />
                <Input value={eq.q} onChange={e => setEq(s => ({ ...s, q: e.target.value }))} placeholder="Search elections…" className="flex-1" data-testid="election-search-input" />
                <Btn variant="secondary" onClick={() => loadElections()} data-testid="election-search-btn"><Search className="w-4 h-4" /></Btn>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="election-results">
                {elections.map(e => (
                  <button key={e.id} onClick={() => attachRace(e.id)} data-testid={`election-${e.id}`}
                    className="w-full text-left p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 transition-colors">
                    <div className="text-sm text-white">{e.name}</div>
                    <div className="text-[11px] text-slate-500">{e.election_date || "Date TBD"} · {e.level} · via {e.source}{e.verification_status === "pending_verification" ? " · pending verification" : ""}</div>
                  </button>
                ))}
                {elections.length === 0 && <div className="text-sm text-slate-500 py-2">No elections match — adjust the filter or request yours below.</div>}
              </div>
              {!reqForm ? (
                <button onClick={() => setReqForm({ state: form.state || "", office: form.office || "", district: "", election_date: "", level: "local", official_url: "" })}
                  className="text-xs text-cyan-300 hover:underline" data-testid="request-election-btn">Can't find your election? Add / request it →</button>
              ) : (
                <div className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="request-election-form">
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="State" value={reqForm.state} onChange={e => setReqForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} />
                    <Input label="Office" value={reqForm.office} onChange={e => setReqForm(f => ({ ...f, office: e.target.value }))} placeholder="City Council" />
                    <Input label="District (optional)" value={reqForm.district} onChange={e => setReqForm(f => ({ ...f, district: e.target.value }))} />
                    <Input label="Election date" type="date" value={reqForm.election_date} onChange={e => setReqForm(f => ({ ...f, election_date: e.target.value }))} />
                    <Input label="Official election URL" value={reqForm.official_url} onChange={e => setReqForm(f => ({ ...f, official_url: e.target.value }))} className="col-span-2" />
                  </div>
                  <Btn onClick={requestElection} disabled={!reqForm.state || !reqForm.office} data-testid="submit-election-request">Submit & attach</Btn>
                </div>
              )}
              <div className="flex justify-between pt-2">
                <span />
                <button onClick={() => setStep(4)} className="text-xs text-slate-500 hover:text-slate-300" data-testid="skip-race-btn">Skip for now →</button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5 text-center py-4" data-testid="verify-email-step">
              <MailCheck className={`w-12 h-12 mx-auto ${verified ? "text-emerald-300" : "text-cyan-300"}`} />
              {verified ? (
                <>
                  <div className="font-display text-xl font-bold text-emerald-300">Official email verified</div>
                  <p className="text-sm text-slate-400">Your entity's verification score just went up. Website verification runs automatically in the background.</p>
                </>
              ) : (
                <>
                  <div className="font-display text-xl font-bold">Verify your official campaign email</div>
                  <p className="text-sm text-slate-400">We'll send a 6-digit code to <b className="text-slate-200">{form.official_email}</b>. Advertising launch and fundraising activation require a verified entity.</p>
                  {!otpSent ? (
                    <Btn onClick={sendOtp} data-testid="send-otp-btn">Send verification code</Btn>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000" data-testid="otp-input"
                        className="w-40 text-center text-2xl tracking-[8px] bg-slate-900/80 border border-white/15 rounded-xl px-3 py-3 focus:border-cyan-400/60 focus:outline-none font-mono" />
                      <Btn onClick={confirmOtp} disabled={otp.length !== 6} data-testid="confirm-otp-btn">Verify</Btn>
                    </div>
                  )}
                  {otpSent && <button onClick={sendOtp} className="text-xs text-slate-500 hover:text-slate-300 block mx-auto">Resend code</button>}
                </>
              )}
              <div className="pt-4 flex items-center justify-center gap-3">
                {!verified && <button onClick={finish} className="text-xs text-slate-500 hover:text-slate-300" data-testid="skip-verify-btn">Skip — verification pending</button>}
                {verified && <Btn onClick={finish} data-testid="finish-onboarding-btn">Enter your workspace <ArrowRight className="w-4 h-4" /></Btn>}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
